<div class="billing-page">

  <app-billing-header></app-billing-header>

  @if (wallet) {

    <div class="billing-grid">

      <!-- Balance -->
      <div class="billing-card billing-card--dark">
        <svg class="wallet-illustration" viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <rect x="10" y="16" width="56" height="38" rx="8" fill="rgba(255,255,255,0.10)"/>
          <rect x="18" y="26" width="60" height="46" rx="10" fill="rgba(255,255,255,0.16)" stroke="rgba(255,255,255,0.28)" stroke-width="1.5"/>
          <path d="M18 40H78V44C78 47.3137 75.3137 50 72 50H24C20.6863 50 18 47.3137 18 44V40Z" fill="rgba(255,255,255,0.10)"/>
          <circle cx="68" cy="45" r="5" fill="var(--color-primary-500)"/>
        </svg>

        <span class="billing-card__overline">Current balance</span>
        <span class="billing-card__balance" [class.billing-card__balance--negative]="walletStatus === 'overdrawn'">
          {{ wallet.balanceCents | money: wallet.currency }}
        </span>

        @switch (walletStatus) {
          @case ('active') {
            <span class="wallet-status-badge wallet-status-badge--active"><span class="wallet-status-badge__dot"></span> Active</span>
          }
          @case ('low') {
            <span class="wallet-status-badge wallet-status-badge--low"><span class="wallet-status-badge__dot"></span> Low balance</span>
          }
          @case ('empty') {
            <span class="wallet-status-badge wallet-status-badge--negative"><span class="wallet-status-badge__dot"></span> No funds</span>
          }
          @case ('overdrawn') {
            <span class="wallet-status-badge wallet-status-badge--negative"><span class="wallet-status-badge__dot"></span> Overdrawn</span>
          }
        }

        <p class="billing-card__desc">
          1 credit = {{ wallet.pricePerCreditCents | money: wallet.currency }}
          @if (requestsLeft > 0) {
            · about {{ requestsLeft | number }} requests left
          }
        </p>

        @switch (walletStatus) {
          @case ('active') {
            <div class="billing-card__info-banner">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14Zm.75-9.75a.75.75 0 10-1.5 0v.5a.75.75 0 001.5 0v-.5ZM7.25 8a.75.75 0 011.5 0v3.25a.75.75 0 01-1.5 0V8Z" fill="currentColor"/>
              </svg>
              Each API call costs 1 credit. Calls that fail upstream aren't charged.
            </div>
          }
          @case ('low') {
            <div class="billing-card__info-banner billing-card__info-banner--warning" role="status">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14Zm.75-9.75a.75.75 0 10-1.5 0v.5a.75.75 0 001.5 0v-.5ZM7.25 8a.75.75 0 011.5 0v3.25a.75.75 0 01-1.5 0V8Z" fill="currentColor"/>
              </svg>
              Top up soon. Calls are refused once your balance reaches {{ 0 | money: wallet.currency }}.
            </div>
          }
          @default {
            <div class="billing-card__info-banner billing-card__info-banner--danger" role="alert">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14Zm.75-9.75a.75.75 0 10-1.5 0v.5a.75.75 0 001.5 0v-.5ZM7.25 8a.75.75 0 011.5 0v3.25a.75.75 0 01-1.5 0V8Z" fill="currentColor"/>
              </svg>
              @if (walletStatus === 'overdrawn') {
                A call already served is charged even if it overdraws. New calls are refused until you add funds.
              } @else {
                New calls are refused until you add funds.
              }
            </div>
          }
        }
      </div>

      <!-- Add funds -->
      @if (membership$ | async; as membership) {
        <div class="billing-card">
          <span class="billing-card__overline">Add funds</span>

          @if (membership.canManageBilling) {
            <div class="amount-pills">
              @for (amt of quickAmounts; track amt) {
                <button
                  type="button"
                  class="amount-pill"
                  [class.amount-pill--active]="selectedAmountCents === amt && !customAmount"
                  (click)="selectAmount(amt)"
                >{{ amt | money: wallet.currency }}</button>
              }
            </div>

            <div class="form-field">
              <label class="form-field__label" for="custom-amount">Or enter a custom amount</label>
              <div class="amount-input-wrap">
                <span class="amount-input-wrap__prefix">{{ currencySymbol(wallet.currency) }}</span>
                <input
                  id="custom-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  [(ngModel)]="customAmount"
                />
              </div>
            </div>

            @if (topUpAmountCents; as cents) {
              <p class="charge-summary">
                You'll be charged <strong>{{ cents | money: wallet.currency }}</strong> on Stripe. Nothing is charged until you confirm there.
              </p>
            }

            <div class="billing-card__actions">
              <button class="btn--primary" type="button" [disabled]="isToppingUp" (click)="addFunds()">
                {{ isToppingUp ? 'Redirecting…' : 'Continue to Stripe' }}
              </button>
            </div>
          } @else {
            <p class="billing-card__desc">Only the organization owner can add funds to the wallet.</p>
          }
        </div>
      }

    </div>

    <!-- Recent transactions -->
    <div class="api-card">
      <div class="api-card__header">
        <div class="api-card__header-stack">
          <span class="api-card__title">Recent transactions</span>
          <span class="api-card__desc">Your latest top-ups, grants, and refunds.</span>
        </div>
        <a routerLink="/billing/transactions" class="btn--secondary">
          View all
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>

      @if (recentTransactions.length) {
        <app-txn-table [rows]="recentTransactions"></app-txn-table>
      } @else {
        <p class="billing-card__desc" style="padding: 24px;">No transactions yet.</p>
      }
    </div>

  } @else if (loadFailed) {

    <div class="api-card">
      <div class="error-card" role="alert">
        <span class="error-card__icon">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14Zm.75-9.75a.75.75 0 10-1.5 0v.5a.75.75 0 001.5 0v-.5ZM7.25 8a.75.75 0 011.5 0v3.25a.75.75 0 01-1.5 0V8Z" fill="currentColor"/>
          </svg>
        </span>
        <p class="error-card__title">We couldn't load your wallet</p>
        <p class="error-card__desc">Check your connection and try again. Your balance and top-ups aren't affected.</p>
        <button class="btn--primary" type="button" (click)="load()">Retry</button>
      </div>
    </div>

  } @else {

    <!-- Loading -->
    <div class="billing-grid" aria-busy="true" aria-label="Loading your wallet">
      @for (i of [0, 1]; track i) {
        <div class="billing-card">
          <app-skeleton width="35%" height="12px"></app-skeleton>
          <app-skeleton width="55%" height="36px"></app-skeleton>
          <app-skeleton width="80%" height="14px"></app-skeleton>
          <app-skeleton width="100%" height="40px" radius="8px"></app-skeleton>
        </div>
      }
    </div>
    <div class="api-card">
      <div class="api-card__header"><app-skeleton width="180px" height="16px"></app-skeleton></div>
      @for (i of [0, 1, 2, 3]; track i) {
        <div class="skeleton-row">
          <app-skeleton width="120px" height="14px"></app-skeleton>
          <app-skeleton width="80px" height="14px"></app-skeleton>
          <app-skeleton width="320px" height="14px"></app-skeleton>
          <app-skeleton width="70px" height="14px"></app-skeleton>
        </div>
      }
    </div>

  }

</div>
